import { useState } from "react";
import {
  RiMailLine,
  RiCloseLine,
  RiCodeLine,
  RiFileCopyLine,
  RiCheckLine,
  RiSaveLine,
} from "react-icons/ri";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const NewTemplateEditor = ({ onClose, onSave }) => {
  const [template, setTemplate] = useState({
    name: "",
    subject: "",
    category: "marketing",
    content: "",
    tags: [],
    variables: [],
  });
  const [viewMode, setViewMode] = useState("edit"); // 'edit' or 'code'
  const [copied, setCopied] = useState(false);
  const [currentTag, setCurrentTag] = useState("");

  const categories = [
    { id: "marketing", label: "Marketing" },
    { id: "newsletters", label: "Newsletters" },
    { id: "notifications", label: "Notifications" },
    { id: "transactional", label: "Transactional" },
  ];

  const handleContentChange = (content) => {
    setTemplate((prev) => ({ ...prev, content }));
    // Extract variables from content - looking for patterns like {variable_name}
    const variableMatches = content.match(/\{([^}]+)\}/g) || [];
    const uniqueVariables = [
      ...new Set(variableMatches.map((v) => v.slice(1, -1))),
    ];
    setTemplate((prev) => ({ ...prev, variables: uniqueVariables }));
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && currentTag.trim()) {
      setTemplate((prev) => ({
        ...prev,
        tags: [...new Set([...prev.tags, currentTag.trim()])],
      }));
      setCurrentTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTemplate((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const getHtmlCode = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${template.subject}</title>
  <style>
    body { 
      font-family: Arial, sans-serif; 
      line-height: 1.6; 
      margin: 0;
      padding: 0;
      background-color: #f4f4f4;
    }
    .container { 
      max-width: 600px; 
      margin: 0 auto; 
      padding: 20px;
      background-color: #ffffff;
    }
    .header { 
      text-align: center; 
      padding: 20px 0; 
    }
    .content { 
      padding: 20px 0; 
    }
    .footer { 
      text-align: center; 
      padding: 20px 0; 
      color: #666; 
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="content">
      ${template.content}
    </div>
  </div>
</body>
</html>`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getHtmlCode());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-secondary-200">
          <div className="flex items-center space-x-3">
            <RiMailLine className="w-6 h-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-secondary-900">
              New Email Template
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex rounded-lg border border-secondary-200 p-1">
              <button
                onClick={() => setViewMode("edit")}
                className={`px-3 py-1.5 rounded ${
                  viewMode === "edit"
                    ? "bg-secondary-100 text-secondary-900"
                    : "text-secondary-600 hover:bg-secondary-50"
                }`}
              >
                Editor
              </button>
              <button
                onClick={() => setViewMode("code")}
                className={`px-3 py-1.5 rounded flex items-center ${
                  viewMode === "code"
                    ? "bg-secondary-100 text-secondary-900"
                    : "text-secondary-600 hover:bg-secondary-50"
                }`}
              >
                <RiCodeLine className="w-4 h-4 mr-1" />
                HTML
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-50 rounded-lg"
            >
              <RiCloseLine className="w-6 h-6 text-secondary-500" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {viewMode === "edit" ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={template.name}
                    onChange={(e) =>
                      setTemplate((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="e.g., Welcome Email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Category
                  </label>
                  <select
                    className="input-field"
                    value={template.category}
                    onChange={(e) =>
                      setTemplate((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={template.subject}
                  onChange={(e) =>
                    setTemplate((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  placeholder="Enter email subject"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Content
                </label>
                <div className="border border-secondary-200 rounded-lg">
                  <ReactQuill
                    value={template.content}
                    onChange={handleContentChange}
                    className="h-64"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {template.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm flex items-center"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-secondary-400 hover:text-secondary-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="input-field"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder="Add tags (press Enter)"
                />
              </div>

              {template.variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Variables Detected
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {template.variables.map((variable) => (
                      <span
                        key={variable}
                        className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-sm"
                      >
                        {`{${variable}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <pre className="bg-secondary-900 text-secondary-100 p-4 rounded-lg overflow-x-auto">
                <code className="text-sm">{getHtmlCode()}</code>
              </pre>
              <button
                onClick={copyToClipboard}
                className="absolute top-4 right-4 p-2 bg-secondary-800 text-secondary-100 rounded-lg hover:bg-secondary-700 transition-colors"
              >
                {copied ? (
                  <RiCheckLine className="w-5 h-5 text-green-500" />
                ) : (
                  <RiFileCopyLine className="w-5 h-5" />
                )}
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-secondary-200 bg-white">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-secondary-700 hover:bg-secondary-50 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(template)}
              disabled={
                !template.name || !template.subject || !template.content
              }
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <RiSaveLine className="w-5 h-5 mr-2" />
              Save Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTemplateEditor;
